/**
 * AssetsTab — Batch #14.325 : registre des immobilisations (cabinet et tenue de livres).
 * Liste + KPI, fiche (tableau d'amortissement, écritures), écriture d'amortissement (mois / exercice), cession, sommaire DPA (annexe 8), PDF.
 */
import { useCallback, useEffect, useState } from "react";
import { FileDown, Plus, Table2, Wand2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Empty, Pill, downloadPdf, errMsg, money, useGlBase } from "../../comptabilite/_shared";
import AssetDialog from "./AssetDialog";
import AssetDetail from "./AssetDetail";
import DepreciationDialog from "./DepreciationDialog";
import CcaSummary from "./CcaSummary";

const METHOD = { linear: "Linéaire", declining: "Dégressif" };

function Kpi({ label, value, testId, tone = "text-slate-800" }) {
  return (
    <div className="bg-white border rounded-xl px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className={`font-mono font-bold text-sm ${tone}`} data-testid={testId}>{value}</div>
    </div>
  );
}

export default function AssetsTab({ accounts, refresh, goTab }) {
  const base = useGlBase();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("active");
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const [depr, setDepr] = useState(false);
  const [cca, setCca] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`${base}/assets`, { params: { status: status || undefined } }); setData(r.data); }
    catch (e) { toast.error(errMsg(e, "Registre indisponible")); }
  }, [base, status]);
  useEffect(() => { load(); }, [load]);
  const reload = () => { load(); refresh?.(); };

  const t = data?.totals;
  return (
    <div className="space-y-4" data-testid="assets-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 flex-1">
          <Kpi label="Actifs en service" value={t ? t.nb : "…"} testId="assets-kpi-nb" />
          <Kpi label="Coût" value={t ? money(t.cost) : "…"} testId="assets-kpi-cost" />
          <Kpi label="Valeur nette comptable" value={t ? money(t.nbv) : "…"} testId="assets-kpi-nbv" tone="text-emerald-700" />
          <Kpi label={`Amort. exercice ${data?.fy_start?.slice(0, 4) || ""}`} value={t ? `${money(t.booked_current_fy)} / ${money(t.book_current_fy)}` : "…"} testId="assets-kpi-book" tone={t && Math.abs(t.booked_current_fy - t.book_current_fy) > 0.005 ? "text-orange-700" : "text-slate-800"} />
          <Kpi label="DPA fiscale exercice" value={t ? money(t.fiscal_current_fy) : "…"} testId="assets-kpi-fiscal" />
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          {[["active", "En service"], ["disposed", "Cédés"], ["", "Tous"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} className={`px-3 py-1.5 text-xs font-semibold ${status === v ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`assets-filter-${v || "all"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Btn tone="slate" icon={Table2} onClick={() => setCca(true)} testId="assets-cca-btn">Sommaire DPA (annexe 8)</Btn>
          <Btn tone="slate" icon={FileDown} busy={busy} onClick={() => downloadPdf(`${base}/assets/register.pdf`, `Registre_immobilisations_${data?.fy_end || ""}.pdf`, setBusy)} testId="assets-pdf-btn">Registre PDF</Btn>
          <Btn tone="amber" icon={Wand2} onClick={() => setDepr(true)} testId="assets-depr-btn" title="Écriture d'amortissement du mois ou de l'exercice">Écriture d'amortissement</Btn>
          <Btn icon={Plus} onClick={() => setEdit({})} testId="assets-new-btn">Nouvel actif</Btn>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        {data && data.items.length === 0 && <Empty text="Aucune immobilisation. Ajoutez un actif (véhicule, équipement, ordinateur…) : l'amortissement comptable et la DPA se calculent automatiquement." testId="assets-empty">
          <Btn icon={Plus} onClick={() => setEdit({})} testId="assets-empty-new">Nouvel actif</Btn></Empty>}
        {data && data.items.length > 0 && (
          <table className="w-full text-sm" data-testid="assets-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-3 py-2">Actif</th><th className="text-left px-3 py-2">Cat. DPA</th><th className="text-left px-3 py-2">Acquis le</th><th className="text-left px-3 py-2">Méthode</th>
                <th className="text-right px-3 py-2">Coût</th><th className="text-right px-3 py-2">Amort. cumulé</th><th className="text-right px-3 py-2">Valeur nette</th><th className="text-right px-3 py-2">Amort. exercice</th><th className="text-right px-3 py-2">DPA exercice</th></tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(a.id)} data-testid={`asset-row-${a.id}`}>
                  <td className="px-3 py-2"><div className="font-medium text-slate-800">{a.name}</div><div className="text-[11px] text-slate-400">{a.supplier}{a.reference ? ` · ${a.reference}` : ""}</div></td>
                  <td className="px-3 py-2"><Pill>{a.cca_class || "—"} · {a.class_rate}%</Pill></td>
                  <td className="px-3 py-2 font-mono text-xs">{a.acquisition_date}</td>
                  <td className="px-3 py-2 text-xs">{METHOD[a.method]} {a.method === "linear" ? `${a.life_years} ans` : `${a.decl_rate}%`}{a.status === "disposed" && <Pill cls="bg-rose-100 text-rose-800 border-rose-200 ml-1">Cédé {a.disposal?.date}</Pill>}</td>
                  <td className="px-3 py-2 text-right font-mono">{money(a.cost)}</td>
                  <td className="px-3 py-2 text-right font-mono">{money(a.accum_book_prior + a.booked_current_fy)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{a.status === "disposed" ? "—" : money(a.nbv)}</td>
                  <td className="px-3 py-2 text-right font-mono" title="comptabilisé / attendu">{a.status === "disposed" ? "—" : <>{money(a.booked_current_fy)} <span className="text-slate-400">/ {money(a.book_current_fy)}</span></>}</td>
                  <td className="px-3 py-2 text-right font-mono">{a.status === "disposed" ? "—" : money(a.fiscal_current_fy)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[11px] text-slate-500">Amortissement comptable : prorata mensuel la 1re année (demi-mois pour le mois d'acquisition), aucun amortissement l'année de la cession. DPA : solde dégressif par catégorie ; 1re année selon la date d'acquisition — demi-année (50 % du taux), demi-année suspendue (2024) ou incitatif à l'investissement accéléré / réaccéléré (150 % du taux, biens acquis depuis 2025 — ex. camion 48 000 $ cat. 10 : 21 600 $). L'écriture d'amortissement (mensuelle ou annuelle) débite 5600 et crédite l'amortissement cumulé de chaque actif.</p>

      {edit && <AssetDialog asset={edit.id ? edit : null} accounts={accounts} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      {detail && <AssetDetail assetId={detail} accounts={accounts} onClose={() => setDetail(null)} onChanged={reload} onEdit={(a) => { setDetail(null); setEdit(a); }} />}
      {depr && <DepreciationDialog fyEnd={data?.fy_end} onClose={() => setDepr(false)} onPosted={() => { setDepr(false); reload(); }} goTab={goTab} />}
      {cca && <CcaSummary fyEnd={data?.fy_end} onClose={() => setCca(false)} />}
    </div>
  );
}
